import React, { useContext, FC } from 'react';

import { Grid, Paper } from '@material-ui/core';
import { RouteComponentProps } from '@reach/router';

import { IClubOfficer } from '../../../../../store/admin/clubs/store';
import { IAdminMembership } from '../../../../../store/admin/memberships/store';
import { IMessageNotification } from '../../../../../store/notification/store';

import { IOption } from '../../../../../components/Select/Select';
import Tooltip from '../../../../../components/Tooltip';
import NewOfficer from './NewOfficer/NewOfficer';
import OfficerRow from './OfficerRow/OfficerRow';
import TransferAdminDialog from './TransferAdminDialog/TransferAdminDialog';
import { GlobalContext } from '../../../../../store';
import { useBoolean } from '../../../../common/Hooks/useBoolean.hook';

interface IProps extends RouteComponentProps {
  clubId: number;
}

const AdminsSection: FC<IProps> = props => {
  const { clubId } = props;
  const {
    actions: {
      adminClubs: { edit, getManagement, createManagement, removeManagement, updateManagement },
      notifications: { push }
    },
    adminClubs: { management },
    adminMemberships,
    user
  } = useContext(GlobalContext);

  const [displayTransferDialog, , toggleTransferAdminDialog] = useBoolean(false);

  const selectOfficer = (value: any) => async () => {
    const { value: owner } = value;

    const response = await edit(clubId, { owner });
    toggleTransferAdminDialog();

    if (response.errors) {
      await push(({
        status: 'unread',
        text: response.errors.adminClubs.msg,
        type: 'error'
      } as unknown) as IMessageNotification);
    } else {
      await getManagement(clubId, true);
      await push(({
        status: 'unread',
        text: 'Club ownership updated successfully',
        type: 'success'
      } as unknown) as IMessageNotification);
    }
  };

  const clubManagement = management[clubId] ? management[clubId] : {};
  const officers = Object.values(clubManagement);

  officers.forEach((officer: IClubOfficer, i: number) => {
    if (officer.isOwner) {
      if (i === 0) {
        return;
      } else {
        const first = officers[0];
        officers[0] = officer;
        officers[i] = first;
      }
    }
  });

  const managementOptions = Object.values(clubManagement)
    .filter((officer: IClubOfficer) => !officer.isOwner)
    .map((officer: IClubOfficer) => ({
      label: `${officer.user.firstName} ${officer.user.lastName}`,
      value: officer.user.id.toString()
    }));

  const membershipOptions = adminMemberships.memberships
    .map((membership: IAdminMembership) => ({
      label: `${membership.user.firstName} ${membership.user.lastName}`,
      value: membership.user.id.toString()
    }))
    .filter((memberOpt: IOption) => {
      const duplicate = managementOptions.find(
        (managerOpt: IOption) => managerOpt.value === memberOpt.value
      );
      return duplicate === undefined;
    });

  const nameOptions = managementOptions.concat(membershipOptions);

  return (
    <div className="admins-section">
      <TransferAdminDialog
        open={displayTransferDialog}
        toggleOpenDialog={toggleTransferAdminDialog}
        nameOptions={nameOptions}
        selectOfficer={selectOfficer}
      />

      <Paper elevation={1} className="paper">
        <div className="title__container override">
          <h3 className="title-tooltip override">Officers and admins permissions settings</h3>

          <Tooltip
            classes={{ fab: 'tooltip-fab--shift-top-2dot5' }}
            sectionKey="OFFICERS_ADMINS.DESCRIPTION"
          />
        </div>

        {clubManagement && officers.length > 0 && (
          <>
            <Grid className="grid head" container={true} spacing={8}>
              <Grid className="grid-item" item={true} xs={12} md={1} />

              <Grid className="grid-item" item={true} xs={12} md={2}>
                Name
              </Grid>

              <Grid className="grid-item" item={true} xs={12} md={2}>
                Position
              </Grid>

              <Grid className="grid-item" item={true} xs={12} md={2}>
                Active Officer
              </Grid>

              <Grid className="grid-item" item={true} xs={12} md={2}>
                Admin permissions
              </Grid>

              <Grid className="grid-item" item={true} xs={12} md={3} />
            </Grid>

            {officers.map((officer: IClubOfficer) => (
              <OfficerRow
                key={officer.id}
                isLoggedOwner={officer.isOwner && officer.user.id === user.id}
                officer={officer}
                removeManagement={removeManagement}
                updateManagement={updateManagement}
                pushNotification={push}
                toggleTransferAdminDialog={toggleTransferAdminDialog}
                memberships={adminMemberships}
              />
            ))}

            <NewOfficer
              clubId={clubId}
              memberships={adminMemberships}
              createManagement={createManagement}
              pushNotification={push}
              clubManagement={clubManagement}
            />
          </>
        )}
      </Paper>
    </div>
  );
};

export default AdminsSection;
